import { useEffect, useMemo, useState } from 'react';

import { DEFAULT_CLIENT_PAGE_SIZE, MAX_SHELL_PAGE_SIZE } from './constants.js';
import type { DataTableServerPagination } from './types.js';

export type UseServerTablePaginationOptions = {
  total: number;
  pageCount?: number;
  pageSizeMax?: number;
  defaultPageSize?: number;
  resetDeps?: readonly unknown[];
  loading?: boolean;
};

/** Server list pagination with fixed page size; DataTable viewport fit no longer mutates API limits. */
export function useServerTablePagination({
  total,
  pageCount,
  pageSizeMax = MAX_SHELL_PAGE_SIZE,
  defaultPageSize = DEFAULT_CLIENT_PAGE_SIZE,
  resetDeps = [],
  loading = false,
}: UseServerTablePaginationOptions): {
  pageIndex: number;
  setPageIndex: (index: number) => void;
  pageSize: number;
  pagination: DataTableServerPagination;
} {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller passes filter deps intentionally
  }, resetDeps);

  const pagination = useMemo(
    (): DataTableServerPagination => ({
      mode: 'server',
      total,
      pageCount,
      pageIndex,
      pageSize,
      pageSizeMax,
      onPageIndexChange: setPageIndex,
      onPageSizeChange: setPageSize,
      loading,
      fitViewport: true,
    }),
    [total, pageCount, pageIndex, pageSize, pageSizeMax, loading],
  );

  return { pageIndex, setPageIndex, pageSize, pagination };
}
